<script>
import { BModal } from 'bootstrap-vue-next';
import axios from 'axios';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
  name: "EditCallModal",
  components: {
    BModal,
    VueDatePicker
  },
  props: {
    call: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['call-updated'],
  data() {
    return {
      formData: {
        id: null,
        created_at: null,
        call_type_id: null,
        final_call_type_id: null,
        radio_readability: 5
      },
      callTypes: [],
      isLoading: false,
      error: null
    }
  },
  mounted() {
    this.loadCallTypes();
    this.initializeForm();
  },
  watch: {
    call: {
      handler() {
        this.initializeForm();
      },
      deep: true
    }
  },
  methods: {
    async loadCallTypes() {
      try {
        const response = await axios.get('/call/type');
        this.callTypes = response.data;
      } catch (error) {
        console.error('Error loading call types:', error);
        this.error = 'Failed to load call types';
      }
    },
    initializeForm() {
      if (this.call && this.call.id) {
        this.formData = {
          id: this.call.id,
          created_at: this.call.created_at,
          call_type_id: this.call.call_type?.id || null,
          final_call_type_id: this.call.final_call_type?.id || null,
          radio_readability: this.call.radio_readability ?? 5
        };
      }
      this.error = null;
    },
    async saveCall() {
      this.isLoading = true;
      this.error = null;

      try {
        await axios.post('/call/', this.formData);

        this.$emit('call-updated');
        this.$refs.modal.hide();

      } catch (error) {
        console.error('Error updating call:', error);
        this.error = error.response?.data?.message || 'Failed to update call';
      } finally {
        this.isLoading = false;
      }
    },
    show() {
      this.initializeForm();
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    onModalHidden() {
      this.initializeForm();
    }
  },
  computed: {
    modalTitle() {
      return 'Edit Call Information';
    }
  }
}
</script>

<template>
  <BModal
    ref="modal"
    :title="modalTitle"
    size="lg"
    @hidden="onModalHidden"
  >
    <!-- Error Alert -->
    <div class="alert alert-danger" v-if="error">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ error }}
    </div>

    <!-- Edit Call Form -->
    <form @submit.prevent="saveCall" id="editCallForm">
      <!-- Date/Time -->
      <div class="row mb-3">
        <label class="col-sm-3 col-form-label">
          <i class="bi bi-calendar-fill me-2"></i>Date:
        </label>
        <div class="col-sm-9">
          <VueDatePicker
            v-model="formData.created_at"
            :enable-time-picker="true"
            :is24="true"
            format="yyyy-MM-dd HH:mm"
            :clearable="false"
          />
        </div>
      </div>

      <!-- Call Type -->
      <div class="row mb-3">
        <label class="col-sm-3 col-form-label">
          <i class="bi bi-fire me-2"></i>Call Type:
        </label>
        <div class="col-sm-9">
          <select
            v-model="formData.call_type_id"
            class="form-select"
            required
          >
            <option :value="null" disabled>Select Call Type</option>
            <option
              v-for="callType in callTypes"
              :key="callType.id"
              :value="callType.id"
            >
              {{ callType.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Final Call Type -->
      <div class="row mb-3">
        <label class="col-sm-3 col-form-label">
          <i class="bi bi-flag-fill me-2"></i>Final Call Type:
        </label>
        <div class="col-sm-9">
          <select
            v-model="formData.final_call_type_id"
            class="form-select"
          >
            <option :value="null">Not Set</option>
            <option
              v-for="callType in callTypes"
              :key="callType.id"
              :value="callType.id"
            >
              {{ callType.name }}
            </option>
          </select>
          <div class="form-text">
            <i class="bi bi-info-circle me-1"></i>
            Final call type can be different from the initial call type
          </div>
        </div>
      </div>

      <!-- Radio Readability -->
      <div class="row mb-4">
        <label class="col-sm-3 col-form-label">
          <i class="bi bi-reception-4 me-2"></i>Radio Readability:
        </label>
        <div class="col-sm-9">
          <div class="d-flex align-items-center gap-3">
            <input
              v-model.number="formData.radio_readability"
              type="range"
              class="form-range flex-grow-1"
              min="0"
              max="5"
              step="1"
            >
            <span class="badge bg-primary fs-6" style="min-width: 60px;">
              {{ formData.radio_readability }} / 5
            </span>
          </div>
          <div class="form-text">
            <i class="bi bi-info-circle me-1"></i>
            Rate the radio signal quality from 0 (worst) to 5 (best)
          </div>
        </div>
      </div>
    </form>

    <!-- Footer Buttons -->
    <template #footer>
      <button
        type="button"
        class="btn btn-secondary"
        @click="hide()"
        :disabled="isLoading"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="editCallForm"
        class="btn btn-primary"
        :disabled="isLoading"
      >
        <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-check-lg me-2"></i>
        {{ isLoading ? 'Saving...' : 'Save Changes' }}
      </button>
    </template>
  </BModal>
</template>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
